import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    OnInit,
    ViewEncapsulation
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { KAL_DIALOG_DATA, KalDate, KalDialogRef } from '@kalidea/kaligraphi';
import gql from 'graphql-tag';
import get from 'lodash-es/get';
import { finalize, tap } from 'rxjs/operators';
import { BaseComponent } from 'src/app/_core/tools/base.component';
import { KalTableHeader } from 'src/app/_shared/modules/kal-table/kal-table.module';
import { UserState } from 'src/app/_user/store/user/user.state';
import { arrayNotEmpty } from 'src/app/utils/array.utils';
import { FormValidator } from 'src/app/validators/form-validator';
import { environment } from 'src/environments/environment';
import { DevisCommandeDto, FactureDto, MutationGetListeFacturesGeneriqueArgs } from 'src/graphql/generated-types';
import { GraphqlService } from 'src/app/_core/services/graphql.service';

export interface SharedCreditsDepositsDialogData<T extends FactureDto> {

    accepted?: boolean;

    appliedDeposits?: { deposit: T, amountToApply: number }[];

    allocationDate?: KalDate;

    invoice: FactureDto;

}

@Component({
    selector: 'app-shared-credits-deposits-dialog',
    templateUrl: './shared-credits-deposits-dialog.component.html',
    styleUrls: ['./shared-credits-deposits-dialog.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SharedCreditsDepositsDialogComponent<T extends FactureDto> extends BaseComponent implements OnInit {

    dataSource: T[] = [];

    columns: KalTableHeader<FactureDto>[] = [
        {label: 'Avoir / Acompte', code: 'Objet'},
        {label: 'Montant initial', code: 'MontantFactureTotal'},
        {label: 'Restant disponible', code: 'Solde'},
        {label: 'Montant à utiliser', code: 'Benef'}, // On met un nom de colonne non utilisé pour afficher la colonne
    ];

    controls: { [billId: number]: FormControl } = {};

    dateControl = new FormControl(new KalDate());

    constructor(private readonly dialogRef: KalDialogRef<SharedCreditsDepositsDialogComponent<T>>,
                protected readonly cdr: ChangeDetectorRef,
                @Inject(KAL_DIALOG_DATA) public data: SharedCreditsDepositsDialogData<T>,
                private readonly graphqlService: GraphqlService) {
        super(cdr);
    }

    get invoiceOrderList(): number[] {
        const orders: DevisCommandeDto[] = this.data?.invoice?.ListeCommande?.DevisCommandeDTO || [];
        return orders.map(o => o.Id);
    }

    get invoiceInitialAmount(): number {
        return this.data?.invoice?.Solde;
    }

    get initialAmountSum(): number {
        return this.dataSource.reduce((sum, deposit) => deposit.MontantFactureTotal + sum, 0);
    }

    get leftAmountSum(): number {
        return this.dataSource.reduce((sum, deposit) => deposit.Solde + sum, 0);
    }

    get toUseAmountSum(): number {
        return Object.keys(this.controls).reduce((sum, billId) => +this.controls[billId].value + sum, 0);
    }

    get invoiceAmountAfterAllocation(): number {
        return this.invoiceInitialAmount - this.toUseAmountSum;
    }

    get invalid(): boolean {
        const invalidControls = Object.keys(this.controls).some(depositId => this.controls[depositId].invalid);
        const noDeposits = this.dataSource.every(deposit => (this.controls[deposit.Id].value || 0) === 0);
        const invalidDate = this.dateControl.invalid;
        const invalidSum = this.invoiceAmountAfterAllocation < 0;

        return invalidControls || invalidDate || invalidSum || noDeposits;
    }

    getCreditDepositLabel(creditDeposit: FactureDto): string {
        if (creditDeposit.IsAccompte && creditDeposit.CommandeAccompte) {
            return 'Acompte commande ' + creditDeposit.CommandeAccompte.Id;
        } else {
            const isRefund = arrayNotEmpty(get(creditDeposit.ListeCommande, 'DevisCommandeDTO', []));
            const prefix = isRefund ? 'Remboursement ' : 'Avoir ';

            let label = prefix + creditDeposit.NumFacture;

            if (creditDeposit.Objet) {
                label += (' - ' + creditDeposit.Objet);
            }

            return label;
        }
    }

    checkAndSaveHeader(): void {
        this.data.accepted = true;

        this.data.appliedDeposits = this.dataSource
            .filter(deposit => this.controls[deposit.Id].value > 0)
            .map(deposit => {
                return {deposit, amountToApply: +this.controls[deposit.Id].value};
            });

        this.data.allocationDate = this.dateControl.value;

        this.dialogRef.close(this.data);
    }

    close(): void {
        this.dialogRef.close(null);
    }

    setMinDateValidator() {
        // la date minimale correspond à la date la plus récente entre la facture et les avoirs sélectionnés
        let minDate = new KalDate(this.data.invoice.DateFacture, environment.date.format.isoQuery);
        const appliedDepositDates = this.dataSource
            .filter(deposit => this.controls[deposit.Id].value > 0)
            .map(deposit => {
                return new KalDate(deposit.DateFacture, environment.date.format.isoQuery);
            });
        if (arrayNotEmpty(appliedDepositDates)) {
            minDate = appliedDepositDates.reduce(
                (prevDate, currDate) => currDate.isAfter(prevDate) ? currDate : prevDate
                , minDate
            );
        }

        this.dateControl.setValidators(FormValidator.minDateValidator(minDate));
        this.dateControl.updateValueAndValidity();
    }

    getAvoirAcomptes(): void {
        this.loading = true;
        this.watch$ = this.graphqlService.mutateOne<'GetListeFacturesGenerique', MutationGetListeFacturesGeneriqueArgs>({
            mutation: gql`
                mutation GetListeFacturesGenerique($contexte: ContextInfosInput, $criteres: CriteresFactureDTOInput) {
                    GetListeFacturesGenerique(contexte: $contexte, criteres: $criteres){
                        FactureDTO {
                            Id
                            NumFacture
                            Objet
                            IsAvoir
                            IsAccompte
                            CEDelegationCible {
                                Id
                            }
                            CEDelegationCreePar {
                                Id
                            }
                            CEDelegationSource {
                                Id
                            }
                            CommandeAccompte {
                                Id
                            }
                            DateFacture
                            MontantFactureTotal
                            Solde
                            ListeCommande {
                                DevisCommandeDTO {
                                    Id
                                }
                            }
                        }
                    }
                }
            `,
            variables: {
                criteres: {
                    BenefId: this.data.invoice.Benef?.Id,
                    IdFournisseur: this.data.invoice.Fournisseur?.Id,
                    TakeCommandeAcompte: true,
                    TakeCommande: true,
                    TakeDelegationCible: true,
                    TakeDelegationCreerPar: true,
                    TakeDelegationSource: true,
                    WithAccompte: true,
                    WithFacturesSupprimees: false,
                    WithAvoir: true,
                    IsFactureSoldeDifferentDeZero: true
                }
            }
        }).pipe(
            tap(GetListeFacturesGenerique => {
                const deposits = (GetListeFacturesGenerique?.FactureDTO || []) as T[];

                // creer forms Controls
                this.controls = {};
                let availableDeposits = deposits;

                if (this.snapshot(UserState.hasSeparatedAccountingModule)) {
                    availableDeposits = deposits.filter(d => {
                        return d.CEDelegationCible === null
                            || d.CEDelegationSource === null
                            || d.CEDelegationCreePar === null
                            || d.CEDelegationCreePar.Id === this.snapshot(UserState.userContext).CurrentCE
                            || d.CEDelegationCreePar.Id !== d.CEDelegationSource.Id
                            || d.CEDelegationCreePar.Id !== d.CEDelegationCible.Id;
                    });
                }

                availableDeposits = availableDeposits.filter(d => {
                    if (d.IsAvoir) {
                        return true;
                    }
                    return d.IsAccompte && !!d.CommandeAccompte;
                });

                this.setMinDateValidator();

                availableDeposits.forEach(deposit => {
                    this.controls[deposit.Id] = new FormControl(null, [
                        Validators.min(0),
                        Validators.max(deposit.Solde)
                    ])

                    this.watch$ = this.controls[deposit.Id].valueChanges.pipe(
                        tap(() => this.setMinDateValidator())
                    );
                });

                this.dataSource = availableDeposits;
            }),
            finalize(() => this.loading = false)
        );
    }

    ngOnInit(): void {
        this.getAvoirAcomptes();
    }
}
